// GS Mandal — Vendor Portal Login (/vendor/login).
// Approved vendors receive credentials by email; they log in here and are
// routed to /vendor/portal.

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import vendorApi, { VENDOR_TOKEN_KEY } from "@/lib/vendorApi";
import { toast } from "sonner";
import { Loader2, LogIn, Building2, ShieldCheck } from "lucide-react";

export default function VendorLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If already logged in, jump to portal.
    if (localStorage.getItem(VENDOR_TOKEN_KEY)) navigate("/vendor/portal", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await vendorApi.post("/vendor/auth/login", {
        email: email.trim().toLowerCase(),
        password: password,
      });
      localStorage.setItem(VENDOR_TOKEN_KEY, data.access_token);
      toast.success("Welcome to the Vendor Portal");
      navigate("/vendor/portal", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vendor-login-page" className="min-h-screen bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#052A2D] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#0D5C63] to-[#083E43] text-white px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#F26419] rounded-full flex items-center justify-center">
              <Building2 size={22} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F26419] font-bold">GS Mandal</div>
              <div className="text-lg font-semibold">Vendor Portal</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="text-sm text-[#3E3E3E]">
            Log in with the credentials emailed to you after your empanelment
            was <b>approved</b>.
          </div>

          <label className="block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">Email *</div>
            <input
              type="email"
              autoComplete="email"
              data-testid="vendor-login-email"
              className="w-full bg-[#FAFAF7] border border-[#E9E2D2] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="block">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">Password *</div>
            <input
              type="password"
              autoComplete="current-password"
              data-testid="vendor-login-password"
              className="w-full bg-[#FAFAF7] border border-[#E9E2D2] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="vendor-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D90368] hover:to-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full shadow-lg shadow-[#F26419]/25 disabled:opacity-60"
          >
            {busy ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : <><LogIn size={14} /> Sign in</>}
          </button>

          <div className="text-[11px] text-[#8a8478] leading-relaxed flex items-start gap-2 pt-2 border-t border-[#E9E2D2]">
            <ShieldCheck size={13} className="text-[#0D5C63] shrink-0 mt-0.5" />
            <span>
              Not empanelled yet? <Link to="/vendors/register" className="text-[#0D5C63] font-semibold underline">Register here</Link>. You will receive
              portal credentials on your email once your application is approved.
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
